<template>
  <!-- v-container (not a bare div): theme-glass.css welds the breadcrumb to the
       page below it via `.app-breadcrumb:has(+ .container)`, and paints the
       sheet via `#app .layout-main__content > .container`. A plain div matches
       neither, which left the strip floating over a full-bleed slab here while
       every other page welded. -->
  <v-container fluid class="rv-page">
    <!-- Alert Banners -->
    <AAlertBanner :messages="alertError" hide-title />

    <AAlertBanner variant="danger" :messages="alertDanger" hide-title />

    <!-- Confirmation Dialog -->
    <TheModalConfirmation ref="confirmDialog" />

    <!-- Header Card -->
    <div class="rv-header">
      <h1 v-if="!loadingDialog" class="rv-header__title">{{ model.add.name }}</h1>
      <div class="rv-header__spacer"></div>
      <span v-if="isTaxpodAdminRoles && !btnDisabled && !editMode">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <button
              class="rv-header__action"
              :disabled="apiSubmit.isLoading"
              @click="toggleSubmitDialog()"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon size="20" color="#16a34a">mdi-check-circle-outline</v-icon>
            </button>
          </template>
          <span>Submit</span>
        </v-tooltip>
      </span>
    </div>

    <!-- Cards Grid -->
    <div class="rv-cards-grid">
      <!-- LEFT: Refund Details -->
      <div class="rv-section">
        <div class="rv-section__header">
          <span class="rv-section__title">Refund Details</span>
        </div>
        <div class="rv-section__body">
          <!-- taxPOD Price -->
          <div class="form-row">
            <label class="form-label">taxPOD Price</label>
            <div class="form-field">
              <v-text-field
                dense
                filled
                disabled
                v-model="formattedPrice"
                :error-messages="errorField.taxpodPrice"
                hide-details="auto"
                class="rv-input rv-input--disabled"
              ></v-text-field>
            </div>
          </div>

          <!-- Refund Remarks -->
          <div class="form-row form-row--top">
            <label class="form-label">Refund Remarks</label>
            <div class="form-field">
              <v-textarea
                v-model="formData.refundRemarks"
                filled
                dense
                no-resize
                rows="5"
                hide-details="auto"
                class="rv-input"
                placeholder="Enter refund remarks..."
              ></v-textarea>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: Attachments -->
      <div class="rv-section">
        <div class="rv-section__header">
          <span class="rv-section__title">Attachments</span>
        </div>
        <div class="rv-section__body">
          <!-- Screenshot Refund Bank -->
          <div class="form-row">
            <label class="form-label">Screenshot Refund Bank</label>
            <div class="form-field">
              <div class="file-card file-card--disabled" :class="formData.ssRefundBankAccDetails ? 'file-card--has-file' : 'file-card--empty'">
                <div class="file-card__icon-wrap">
                  <div class="file-card__icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                  </div>
                </div>
                <div class="file-card__info">
                  <span class="file-card__name">{{ formData.ssRefundBankAccDetails ? formData.ssRefundBankAccDetails.name : 'No file selected' }}</span>
                  <span class="file-card__hint">Pre-filled (read-only)</span>
                </div>
                <div class="file-card__actions">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <button class="file-card__btn" @click="previewFile('ssRefundBankAccDetails')" v-on="on">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                      </button>
                    </template>
                    <span>Preview</span>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </div>

          <!-- HRDC Invoice -->
          <div class="form-row">
            <label class="form-label">HRDC Invoice</label>
            <div class="form-field">
              <div class="file-card file-card--disabled" :class="formData.manualInvoice ? 'file-card--has-file' : 'file-card--empty'">
                <div class="file-card__icon-wrap">
                  <div class="file-card__icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                  </div>
                </div>
                <div class="file-card__info">
                  <span class="file-card__name">{{ formData.manualInvoice ? formData.manualInvoice.name : 'No file selected' }}</span>
                  <span class="file-card__hint">Pre-filled (read-only)</span>
                </div>
                <span class="file-card__tag" v-if="formData.manualInvoiceToHrdc">{{ formData.manualInvoiceToHrdc }}</span>
                <div class="file-card__actions">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <button class="file-card__btn" @click="previewFile('manualInvoice')" v-on="on">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                      </button>
                    </template>
                    <span>Preview</span>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </div>

          <!-- Client/taxPOD Invoice -->
          <div class="form-row">
            <label class="form-label">Client/taxPOD Invoice</label>
            <div class="form-field">
              <div
                class="file-card file-card--disabled"
                :class="formData.autoGeneratedTaxpodInvoice && formData.autoGeneratedTaxpodInvoice.length > 0 ? 'file-card--has-file' : 'file-card--empty'"
              >
                <div class="file-card__icon-wrap">
                  <div class="file-card__icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                  </div>
                </div>
                <div class="file-card__info">
                  <span class="file-card__name">{{ formData.autoGeneratedTaxpodInvoice && formData.autoGeneratedTaxpodInvoice.length > 0 ? formData.autoGeneratedTaxpodInvoice.map(f => f.name).join(', ') : 'No file selected' }}</span>
                  <span class="file-card__hint">Pre-filled (read-only)</span>
                </div>
                <span class="file-card__tag" v-if="formData.autoGeneratedTaxpodInvoiceNo">{{ formData.autoGeneratedTaxpodInvoiceNo }}</span>
                <div class="file-card__actions">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <button class="file-card__btn" @click="previewFile('autoGeneratedTaxpodInvoice')" v-on="on">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                      </button>
                    </template>
                    <span>Preview</span>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </div>

          <!-- Credit Note -->
          <div class="form-row">
            <label class="form-label">Credit Note</label>
            <div class="form-field">
              <div
                class="file-card file-card--disabled"
                :class="formData.creditNote && formData.creditNote.length > 0 ? 'file-card--has-file' : 'file-card--empty'"
              >
                <div class="file-card__icon-wrap">
                  <div class="file-card__icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                  </div>
                </div>
                <div class="file-card__info">
                  <span class="file-card__name">{{ formData.creditNote && formData.creditNote.length > 0 ? formData.creditNote.map(f => f.name).join(', ') : 'No file selected' }}</span>
                  <span class="file-card__hint">Pre-filled (read-only)</span>
                </div>
                <span class="file-card__tag file-card__tag--amber" v-if="formData.creditNoteNo">{{ formData.creditNoteNo }}</span>
                <div class="file-card__actions">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <button class="file-card__btn" @click="previewFile('creditNote')" v-on="on">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                      </button>
                    </template>
                    <span>Preview</span>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Advice From HRDC (editable) -->
          <div class="form-row">
            <label class="form-label">Payment Advice From HRDC</label>
            <div class="form-field">
              <div
                class="file-card"
                :class="formData.paymentAdviceFromHrdc ? 'file-card--has-file' : 'file-card--empty'"
                @drop.prevent="addDropFile($event, 'paymentAdviceFromHrdc', null)"
                @dragover.prevent
              >
                <div class="file-card__icon-wrap">
                  <div class="file-card__icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                  </div>
                </div>
                <div class="file-card__info">
                  <span class="file-card__name">{{ formData.paymentAdviceFromHrdc ? formData.paymentAdviceFromHrdc.name : 'No file selected' }}</span>
                  <span class="file-card__hint">Any file type, drag & drop supported</span>
                </div>
                <div class="file-card__actions">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <label class="file-card__btn" v-on="on">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                        <input type="file" class="file-card__hidden" @change="handleFileChange($event, 'paymentAdviceFromHrdc')" />
                      </label>
                    </template>
                    <span>Upload</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <button class="file-card__btn" @click="previewFile('paymentAdviceFromHrdc')" v-on="on">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                      </button>
                    </template>
                    <span>Preview</span>
                  </v-tooltip>
                </div>
              </div>
              <div v-if="errorField.paymentAdviceFromHrdc" class="rv-select__error">{{ Array.isArray(errorField.paymentAdviceFromHrdc) ? errorField.paymentAdviceFromHrdc.join(', ') : errorField.paymentAdviceFromHrdc }}</div>
            </div>
          </div>

          <!-- Payment Advice From Client (editable) -->
          <div class="form-row">
            <label class="form-label">Payment Advice From Client</label>
            <div class="form-field">
              <div
                class="file-card"
                :class="formData.paymentAdviceFromClient ? 'file-card--has-file' : 'file-card--empty'"
                @drop.prevent="addDropFile($event, 'paymentAdviceFromClient', null)"
                @dragover.prevent
              >
                <div class="file-card__icon-wrap">
                  <div class="file-card__icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                  </div>
                </div>
                <div class="file-card__info">
                  <span class="file-card__name">{{ formData.paymentAdviceFromClient ? formData.paymentAdviceFromClient.name : 'No file selected' }}</span>
                  <span class="file-card__hint">Any file type, drag & drop supported</span>
                </div>
                <div class="file-card__actions">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <label class="file-card__btn" v-on="on">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                        <input type="file" class="file-card__hidden" @change="handleFileChange($event, 'paymentAdviceFromClient')" />
                      </label>
                    </template>
                    <span>Upload</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <button class="file-card__btn" @click="previewFile('paymentAdviceFromClient')" v-on="on">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                      </button>
                    </template>
                    <span>Preview</span>
                  </v-tooltip>
                </div>
              </div>
              <div v-if="errorField.paymentAdviceFromClient" class="rv-select__error">{{ Array.isArray(errorField.paymentAdviceFromClient) ? errorField.paymentAdviceFromClient.join(', ') : errorField.paymentAdviceFromClient }}</div>
            </div>
          </div>

          <!-- Payment Advice Type -->
          <div class="form-row">
            <label class="form-label">Payment Advice Type</label>
            <div class="form-field">
              <v-select
                :items="paymentAdviceItems"
                v-model="formData.paymentAdviceType"
                filled
                dense
                hide-details="auto"
                class="rv-input"
                :error-messages="errorField.paymentAdviceType"
              ></v-select>
            </div>
          </div>

          <!-- Other Attachments (editable, multiple) -->
          <div class="form-row">
            <label class="form-label">Other Attachments</label>
            <div class="form-field">
              <div
                class="file-card"
                :class="formData.refundOtherAttachments && formData.refundOtherAttachments.length > 0 ? 'file-card--has-file' : 'file-card--empty'"
                @drop.prevent="addDropFile($event, 'refundOtherAttachments', null)"
                @dragover.prevent
              >
                <div class="file-card__icon-wrap">
                  <div class="file-card__icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__svg">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                    </svg>
                  </div>
                </div>
                <div class="file-card__info">
                  <span class="file-card__name">{{ formData.refundOtherAttachments && formData.refundOtherAttachments.length > 0 ? formData.refundOtherAttachments.map(f => f.name).join(', ') : 'No file selected' }}</span>
                  <span class="file-card__hint">e.g. Authorization Letter, drag & drop supported (multiple)</span>
                </div>
                <div class="file-card__actions">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <label class="file-card__btn" v-on="on">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="file-card__btn-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>
                        <input type="file" class="file-card__hidden" multiple @change="handleFileChange($event, 'refundOtherAttachments', true)" />
                      </label>
                    </template>
                    <span>Upload</span>
                  </v-tooltip>
                </div>
              </div>
              <div v-if="errorField.refundOtherAttachments" class="rv-select__error">{{ Array.isArray(errorField.refundOtherAttachments) ? errorField.refundOtherAttachments.join(', ') : errorField.refundOtherAttachments }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden Date Picker Section -->
    <div v-show="false">
      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        :return-value.sync="date"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <div
            class="rv-datepicker__trigger"
            v-bind="attrs"
            v-on="on"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="rv-datepicker__icon">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
            </svg>
            <span v-if="formData.refundEmailSubmissionDate" class="rv-datepicker__text">{{ formData.refundEmailSubmissionDate }}</span>
            <span v-else class="rv-datepicker__placeholder">Select date...</span>
          </div>
        </template>
        <v-date-picker
          v-model="formData.refundEmailSubmissionDate"
          no-title
          scrollable
          color="var(--app-accent)"
        >
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="menu = false">Cancel</v-btn>
          <v-btn text color="primary" @click="$refs.menu.save(date)">OK</v-btn>
        </v-date-picker>
      </v-menu>
    </div>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc/objects/globalData";
import TheModalConfirmation from "@/components/common/TheModalConfirmation.vue";

export default {
  mixins: [hrdcFunction],
  components: {
    TheModalConfirmation,
  },
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
    formattedPrice() {
      return `RM ${parseFloat(this.formData.taxpodPrice).toLocaleString()}`;
    },
  },
  props: ["params"],
  data: () => ({
    apiGetApplicationSingleDetail: new Api(),
    apiGetRefundAttachment: new Api(),
    apiScanPdfText: new Api(),
    apiSubmit: new Api(),
    apiDownloadOrPreviewAttachment: new Api(),
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    alertError: [],
    alertDanger: [],
    date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10),
    menu: false,
    formData: {
      applicationNo: null,
      taxpodPrice: null,
      manualInvoiceToHrdc: null,
      refundEmailSubmissionDate: new Date(
        Date.now() - new Date().getTimezoneOffset() * 60000
      )
        .toISOString()
        .substr(0, 10),
      autoGeneratedTaxpodInvoice: [],
      autoGeneratedTaxpodInvoiceNo: null,
      paymentAdviceFromHrdc: null,
      paymentAdviceFromClient: null,
      paymentAdviceType: "Stripe",
      creditNote: [],
      creditNoteNo: null,
      manualInvoice: {
        name: "HRDC Invoice",
      },
      ssRefundBankAccDetails: {
        name: "Screenshot of Refund Bank Account Details",
      },
      refundRemarks: null,
      refundOtherAttachments: [],
      paymentReceivedDate: null,
      oldCompanyName: null,
      oldSsmNo: null,
      bankUuid: null,
      oldBankAccount: null,
      oriCompanyName: null,
      oldBankAccountHolderName: null,
    },
    errorField: {
      applicationNo: null,
      taxpodPrice: null,
      refundEmailSubmissionDate: null,
      autoGeneratedTaxpodInvoice: null,
      autoGeneratedTaxpodInvoiceNo: null,
      paymentAdviceFromHrdc: null,
      paymentAdviceFromClient: null,
      paymentAdviceType: null,
      creditNote: null,
      creditNoteNo: null,
      refundOtherAttachments: null,
    },
    loadingDialog: false,
    refundEmailSubmissionSubmitDialog: false,
    editMode: false,
    btnDisabled: false,
    paymentAdviceItems: null,
  }),
  async created() {
    this.loadingDialog = !this.loadingDialog;
    this.formData.applicationNo = this.$route.query._ausk;
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Assign payment advice options.
    this.paymentAdviceItems = this.paymentAdviceOpts;

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiSubmit: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/refund_email_submission/create-refund-email-submission`,
        method: "post",
      },
      apiGetApplicationSingleDetail: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-single-application`,
        method: "post",
        params: {
          applicationUuid: this.$route.query._ausk,
        },
      },
      apiScanPdfText: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/refund_email_submission/scan-pdf`,
        method: "post",
      },
      apiGetRefundAttachment: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/get-attachment-array`,
        method: "post",
      },
      apiDownloadOrPreviewAttachment: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/azure-download-or-preview-application-attachment`,
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.stages = await this.hrdcData.allStages;
    const currentStageId =
      this.hrdcData.stageIndex[this.$store.state.hrdc.data.stageId];
    this.rolesAssignation(this.auth.roles);
    if (!this.isTaxpodAdminRoles) {
      this.alertError.push("You are not allowed to perform this action!");
      this.btnDisabled = true;
    }

    if (currentStageId >= this.hrdcData.stageSlug.refundEmailSubmission) {
      this.alertError.push("You already submitted this form");
      this.editMode = true;
    }

    if (currentStageId == this.hrdcData.stageSlug.creditNoteDocumented) {
      if (currentStageId < this.hrdcData.stageSlug.creditNoteDocumented) {
        this.alertError.push("Please complete previous step");
        this.btnDisabled = true;
      }
    } else if (currentStageId == this.hrdcData.stageSlug.creditNoteGenerated) {
      if (currentStageId < this.hrdcData.stageSlug.creditNoteGenerated) {
        this.alertError.push("Please complete previous step");
        this.btnDisabled = true;
      }
    }

    // If edit form to resend email submission.
    this.apiGetApplicationSingleDetail.setCallbackCompleted((response) => {
      try {
        const { data, company } = response;

        if (!this.staticTaxpodPricing.includes(Number(data.totalPrice))) {
          this.alertDanger.push("Detected Unusual Price, Please Take Note");
        }

        const attachments = {};
        let otherRefundAttnCount = 0;
        const stagePriorities = [
          this.hrdcData.stagePriority.creditNoteGenerated,
          this.hrdcData.stagePriority.autoGeneratedTaxpodInvoice,
          this.hrdcData.stagePriority.paymentAdviceFromHrdc,
          this.hrdcData.stagePriority.paymentAdviceFromClient,
          this.hrdcData.stagePriority.refundEmailOtherAttachments,
        ];

        this.formData.taxpodPrice = data.totalPrice;
        this.formData.manualInvoiceToHrdc = data.manualInvoiceToHrdc;
        this.formData.autoGeneratedTaxpodInvoiceNo =
          data.autoGeneratedTaxpodInvoiceNo;
        this.formData.creditNoteNo = data.creditNoteNo;
        // Only override the default (e.g. "Stripe") when a value was already
        // saved (edit/resubmit). On a first submission the DB value is null,
        // and clobbering the default here would submit null back to the server.
        if (data.paymentAdviceFromClientType) {
          this.formData.paymentAdviceType = data.paymentAdviceFromClientType;
        }
        this.formData.oldCompanyName = this.capitalizeFirstLetter(
          company.oldCompanyName
        );
        this.formData.oldSsmNo = company.oldSsmNo;
        this.formData.bankUuid = data.bankUuid;
        this.formData.oldBankAccount = data.oldBankAccount;
        this.formData.refundRemarks = data.refundRemarks;

        if (this.editMode) {
          this.formData.refundEmailSubmissionDate = moment(
            data.ApplicationLog.find(
              (obj) =>
                obj.stageId == this.hrdcData.stagePriority.refundEmailSubmission
            ).startDate
          ).format("YYYY-MM-DD");
        } else {
          let paymentReceivedDate = data.ApplicationLog.find(
            (item) =>
              item.stageId ==
              this.hrdcData.stagePriority.paymentReceivedFromHrdc
          );

          if (paymentReceivedDate) {
            this.formData.paymentReceivedDate = moment(
              paymentReceivedDate.startDate,
              "YYYY-MM-DD HH:mm:ssZ"
            ).format("D MMMM YYYY");
          }

          // Assign Original company name filled by applicant.
          this.formData.oriCompanyName = data.oriCompanyName;

          this.formData.oldBankAccountHolderName =
            data.oldBankAccountHolderName;

          // Define auto generated taxpod invoice & credit note attachments.
          let taxpodInvoiceFile = null;
          let taxpodInvoice = data.ApplicationAttachment.filter(
            (item) =>
              item.stageId ==
              this.hrdcData.stagePriority.autoGeneratedTaxpodInvoice
          );

          if (taxpodInvoice.length > 0) {
            taxpodInvoiceFile = taxpodInvoice;
          }

          // Construct params to retrieve taxpod invoice.
          attachments[this.hrdcData.stagePriority.autoGeneratedTaxpodInvoice] =
            {
              fileUrl: taxpodInvoiceFile.map((item) => item.fileUrl),
              fileName: "Client/taxPOD Invoice",
            };
        }

        for (const stageId of stagePriorities) {
          let filesArr = [];
          const attachment = data.ApplicationAttachment.filter(
            (item) => item.stageId == stageId
          );

          if (attachment.length > 0) {
            for (const atch of attachment) {
              // Cater for other refund attachments.
              if (
                stageId ==
                this.hrdcData.stagePriority.refundEmailOtherAttachments
              ) {
                if (!attachments[stageId]) {
                  attachments[stageId] = {};
                }

                attachments[stageId][otherRefundAttnCount] = {
                  fileUrl: atch.fileUrl,
                  fileName: atch.oriFileName,
                };
                otherRefundAttnCount++;
              } else {
                filesArr.push(atch.fileUrl);

                attachments[stageId] = {
                  fileName: attachment.oriFileName,
                };
              }
            }

            attachments[stageId].fileUrl = filesArr;
          }
        }

        // Get refund email attachment.
        this.apiGetRefundAttachment.setParams({
          applicationUuid: this.$route.query._ausk,
          attachments,
        });

        this.apiGetRefundAttachment.setCallbackCompleted((response) => {
          try {
            const { status, attn } = response;
            if (status) {
              for (const item of attn) {
                // Create a Blob object from the data
                const blob = new Blob([new Uint8Array(item.dataArray)]);

                let fileName = null;
                let file = null;

                // Cater for other refund email attachment.
                if (item.other == true) {
                  switch (item.type) {
                    case "refundEmailOtherAttachments":
                      file = new File([blob], item.fileName, {
                        type: `application/${item.extension}`,
                      });

                      this.formData.refundOtherAttachments.push(file);
                      break;
                  }
                  continue;
                }

                switch (Number(item.type)) {
                  case this.hrdcData.stagePriority.autoGeneratedTaxpodInvoice:
                    fileName = "Client/taxPOD Invoice";
                    // Create the File object
                    file = new File([blob], fileName, {
                      type: `application/${item.extension}`,
                    });

                    this.formData.autoGeneratedTaxpodInvoice.push(file);
                    break;

                  case this.hrdcData.stagePriority.creditNote:
                  case this.hrdcData.stagePriority.creditNoteGenerated:
                    fileName = "Credit Note";
                    // Create the File object
                    file = new File([blob], fileName, {
                      type: `application/${item.extension}`,
                    });

                    this.formData.creditNote.push(file);
                    break;

                  case this.hrdcData.stagePriority.paymentAdviceFromHrdc:
                    fileName = "Payment Advice From HRDC";
                    // Create the File object
                    file = new File([blob], fileName, {
                      type: `application/${item.extension}`,
                    });

                    this.formData.paymentAdviceFromHrdc = file;
                    break;

                  case this.hrdcData.stagePriority.paymentAdviceFromClient:
                    fileName = "Payment Advice From Client";
                    // Create the File object
                    file = new File([blob], fileName, {
                      type: `application/${item.extension}`,
                    });

                    this.formData.paymentAdviceFromClient = file;
                    break;
                }
              }
            }
          } catch (err) {
            console.log(err);
          }
        });

        this.apiGetRefundAttachment.fetch();
      } catch (err) {
        console.log(err);
      } finally {
        this.loadingDialog = !this.loadingDialog;
      }
    });
    this.apiGetApplicationSingleDetail.fetch();
  },
  methods: {
    toggleSubmitDialog() {
      this.$refs.confirmDialog.open({
        title: this.model.name.singular,
        message:
          "Please confirm that you wish to Draft this Refund Email for next approval.",
        options: {
          btnDisagree: "Cancel",
          btnAgree: "Draft",
        },
        agree: () => {
          this.modalRefundEmailSubmissionSubmit();
        },
        disagree: () => {},
      });
    },
    handleFileChange(event, key, multiple = false) {
      if (multiple) {
        const files = event.target.files;
        if (files && files.length > 0) {
          for (const file of files) {
            this.formData[key].push(file);
          }
          this.clearErrorMsg(key);
        }
      } else {
        const file = event.target.files[0];
        if (file) {
          this.formData[key] = file;
          this.checkAttachment(file, key);
        }
      }
    },
    previewFile(fileType) {
      try {
        const fileData = this.formData[fileType];
        if (!fileData || (Array.isArray(fileData) && fileData.length === 0)) {
          this.$store.dispatch("showMessage", {
            message: "No Preview Available",
            messageType: "info",
          });
          return;
        }

        // For array files (multiple), preview first one
        const file = Array.isArray(fileData) ? fileData[0] : fileData;
        const url = URL.createObjectURL(file);
        const newWindow = window.open(url, "_blank");

        if (newWindow) {
          newWindow.onload = function () {
            newWindow.addEventListener("beforeunload", function () {
              URL.revokeObjectURL(url);
            });
          };
        }
      } catch {
        this.$store.dispatch("showMessage", {
          message: "Failed to preview file",
          messageType: "error",
          timeout: 2000,
        });
      }
    },
    clearErrorMsg(field = null) {
      if (!field) {
        for (let error in this.errorField) {
          this.errorField[error] = null;
        }
      } else {
        this.errorField[field] = null;
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = null;
        this.errorField[key] = response.data[key];
      }
    },
    modalRefundEmailSubmissionSubmit() {
      this.refundEmailSubmissionSubmitDialog =
        !this.refundEmailSubmissionSubmitDialog;
      this.submit();
    },
    redirectBack() {
      let route = "HrdcApplication";
      if (this.params.backTo) {
        route = this.params.backTo;
      }

      this.$store.commit("assignDeveloperData", {
        paginationPageNo: this.$route.query.currentPageNo,
      });

      this.$router.push({
        name: route,
        params: {
          modelKey: this.model.key,
          backTo: "",
        },
        query: {
          viewId: this.$store.state.hrdc.data.viewId,
          _ausk: this.$route.query._ausk,
        },
      });
    },
    addDropFile(e, key, matchExtType) {
      const files = e.dataTransfer.files;
      for (const file of files) {
        if (matchExtType && !file.type.match(matchExtType)) {
          switch (matchExtType) {
            case "pdf.*":
              this.errorField[key] = "Please select PDF file format";
              break;
            case "image/*":
              this.errorField[key] = "Please select only image files";
              break;
          }
          continue;
        }

        if (Array.isArray(this.formData[key])) {
          this.formData[key].push(file);
        } else {
          this.formData[key] = file;
          this.checkAttachment(file, key);
        }
      }

      // Clear error if at least one valid file dropped
      if (files.length > 0) {
        this.clearErrorMsg(key);
      }
    },
    addDropMultipleFile(e, key, fileType, matchExtType) {
      let files = e.dataTransfer.files;

      for (const file of files) {
        if (matchExtType) {
          if (!file.type.match(matchExtType)) {
            switch (matchExtType) {
              case "pdf.*":
                this.errorField[key] = "Please select pdf file format";
                break;
              case "image/*":
                this.errorField[key] = "Please select only image";
                break;
            }
            return;
          }
        }

        this.formData[key].push(file);

        if (fileType) {
          this.scanPdf(e, key, fileType);
        }
      }

      if (e) {
        this.clearErrorMsg(key);
      }
    },
    scanPdf($event, fileType, displayFileInvoiceNo) {
      this.clearErrorMsg(fileType);
      this.clearErrorMsg(displayFileInvoiceNo);
      if ($event) {
        this.loadingDialog = !this.loadingDialog;
        const fd = new FormData();

        let counter = 0;
        for (const file of this.formData[fileType]) {
          fd.append(`${fileType}_${counter}`, file);
          counter++;
        }

        fd.append("fileType", fileType);
        fd.append("counter", counter);

        this.apiScanPdfText.setParams(fd);
        this.apiScanPdfText.setCallbackCompleted((response) => {
          try {
            const { data } = response;
            if (response.status) {
              this.formData[displayFileInvoiceNo] = data;
            } else {
              this.formData[displayFileInvoiceNo] = null;
              this.$store.dispatch("showMessage", {
                message: "Unable to scan text!",
                messageType: "error",
                timeout: 2000,
              });
            }
            this.loadingDialog = !this.loadingDialog;
          } catch (err) {
            console.log(err);
          }
        });
        this.apiScanPdfText.fetch();
      } else {
        // Empty the invoice no. if remove attachment.
        this.formData[displayFileInvoiceNo] = null;
      }
    },
    isValidURL(str) {
      try {
        new URL(str);
        return true;
      } catch (error) {
        return false;
      }
    },
    previewOrDownloadAttachment(fileUrl) {
      this.loadingDialog = !this.loadingDialog;

      // Check if a valid url, directly open it.
      const isValidUrl = this.isValidURL(fileUrl);
      if (isValidUrl) {
        window.open(fileUrl, "_blank");
      } else {
        this.$axios
          .get(this.apiDownloadOrPreviewAttachment.url, {
            responseType: "blob",
            headers: {
              "Content-Type": "application/json",
            },
            params: {
              filePath: fileUrl,
              isImage: false,
            },
          })
          .then((response) => {
            const blobUrl = URL.createObjectURL(
              new Blob([response.data], { type: "application/pdf" })
            );

            window.open(blobUrl, "_blank");
          })
          .catch((error) => {
            if ((error.response.status == 404) | true) {
              this.$store.dispatch("showMessage", {
                message:
                  "Failed to Preview/Download attachment, kindly contact administrator",
                messageType: "error",
                timeout: 2000,
              });
            }
          });
      }

      this.loadingDialog = !this.loadingDialog;
    },
    capitalizeFirstLetter(str) {
      return str
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    checkAttachment(e, field) {
      if (e) {
        this.clearErrorMsg(field);
      }
    },
    getFileExt(fileType) {
      if (fileType) {
        // Get the file extension type.
        const fileSplit = fileType.split("/");
        return fileSplit[1];
      }

      return fileType;
    },
    async submit() {
      // const changeFileExtensions = ["paymentAdviceFromClient"];

      // for (const file of changeFileExtensions) {
      //   if (this.formData[file]) {
      //     let oriFileExt = await this.getOriginalAttachmentFileExtension(
      //       this.formData[file]
      //     );

      //     this.formData[file] = new File(
      //       [this.formData[file]],
      //       this.formData[file].name,
      //       {
      //         type: oriFileExt,
      //       }
      //     );
      //   }
      // }

      this.alertError = [];
      this.loadingDialog = !this.loadingDialog;

      for (const file of this.REFUND_TAXPOD_ATTACHMENTS) {
        if (!this.formData[file]) {
          this.alertError.push("Please fill in all the fields!");
          this.hideLoadingDialog();
          return false;
        }
      }

      // Generate Form Data to handle multipart/form-data
      const fd = new FormData();
      fd.append("applicationUuid", this.$route.query._ausk);
      fd.append("totalPrice", this.formData.taxpodPrice);

      // Loop taxPOD invoice.
      let taxpodInvoiceCounter = 0;
      for (const taxpodInvoice of this.formData.autoGeneratedTaxpodInvoice) {
        fd.append(
          `autoGeneratedTaxpodInvoice_${taxpodInvoiceCounter}`,
          taxpodInvoice
        );
        taxpodInvoiceCounter++;
      }
      fd.append("taxpodInvoiceCounter", taxpodInvoiceCounter);

      fd.append(
        "autoGeneratedTaxpodInvoice",
        this.formData.autoGeneratedTaxpodInvoice
      );

      fd.append(
        "autoGeneratedTaxpodInvoiceNo",
        this.formData.autoGeneratedTaxpodInvoiceNo
      );

      let cnCounter = 0;
      for (const cn of this.formData.creditNote) {
        fd.append(`creditNote_${cnCounter}`, cn);
        cnCounter++;
      }

      fd.append("cnCounter", cnCounter);
      fd.append("creditNoteNo", this.formData.creditNoteNo);
      fd.append(
        "refundEmailSubmissionDate",
        this.formData.refundEmailSubmissionDate
      );
      fd.append(
        "paymentAdviceFromClient",
        this.formData.paymentAdviceFromClient
      );
      fd.append("paymentAdviceFromHrdc", this.formData.paymentAdviceFromHrdc);
      fd.append("paymentAdviceType", this.formData.paymentAdviceType);
      fd.append("referById", this.auth.uuid);
      fd.append("editMode", this.editMode);
      fd.append("refundRemarks", this.formData.refundRemarks);

      // Assign multiple attachments to Form Data.
      for (let i = 0; i < this.formData.refundOtherAttachments.length; i++) {
        fd.append(
          `refundOtherAttachments_${i}`,
          this.formData.refundOtherAttachments[i]
        );
      }

      this.apiSubmit.setParams(fd);
      this.apiSubmit.setCallbackCompleted((response) => {
        try {
          this.clearErrorMsg();
          this.loadingDialog = !this.loadingDialog;
          if (!response.status) {
            this.showErrorMessage(response);
            if (response.message) {
              this.$store.dispatch("showMessage", {
                message: response.message,
                messageType: "error",
                timeout: 2000,
              });
            }
          }

          if (response.status) {
            this.$store.commit("assignDeveloperData", {
              paginationPageNo: this.$route.query.currentPageNo,
            });

            this.$router.push({
              name: "HrdcApplication",
              query: {
                viewId: this.$store.state.hrdc.data.viewId,
              },
            });
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
          }
        } catch (err) {
          console.log(err);
        }
      });
      this.apiSubmit.fetch();
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .rv-page {
  background-color: var(--app-page);
}

.dark-mode .rv-header {
  background: var(--app-elevated);
}

.dark-mode .rv-header__back {
  background: var(--app-form-tile);
  border: 1px solid var(--app-border);
}

.dark-mode .rv-header__back:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-border-strong);
}

.dark-mode .rv-header__title {
  color: var(--app-text);
}

.dark-mode .rv-header__action {
  background: var(--app-form-tile);
  border: 1px solid var(--app-border);
}

.dark-mode .rv-header__action:hover {
  background: var(--app-accent-soft);
}

.dark-mode .rv-section {
  background: var(--app-elevated);
}

.dark-mode .rv-section__body {
  background: var(--app-card);
}

.dark-mode .form-label {
  color: var(--app-text);
}

.dark-mode .form-label--sub {
  color: var(--app-text-muted);
}

.dark-mode .rv-input >>> .v-input__slot {
  background: var(--app-field) !important;
  border: 1px solid var(--app-field-border) !important;
}

.dark-mode .rv-input >>> .v-text-field__slot input::placeholder,
.dark-mode .rv-input >>> .v-text-field__slot textarea::placeholder {
  color: var(--app-field-placeholder) !important;
}

.dark-mode .rv-input >>> .v-label {
  color: var(--app-field-placeholder) !important;
}

.dark-mode .rv-input--disabled >>> .v-input__slot {
  background: var(--app-field-disabled) !important;
  border: 1px dashed var(--app-field-disabled-border) !important;
}

.dark-mode .rv-input--disabled >>> .v-text-field__slot input,
.dark-mode .rv-input--disabled >>> .v-text-field__slot textarea {
  color: var(--app-text-faint) !important;
}

.dark-mode .rv-input.v-select >>> .v-input__slot {
  background: var(--app-field) !important;
  border: 1px solid var(--app-field-border) !important;
}

.dark-mode .file-card__tag {
  background: var(--app-accent-soft);
}

.dark-mode .file-card__tag--amber {
  background: var(--app-warning-bg);
}

.dark-mode .file-card {
  border: 1px solid var(--app-border);
  background: var(--app-surface);
}

.dark-mode .file-card:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .file-card--disabled {
  border: 1px dashed var(--app-border);
  background: var(--app-form-tile);
}

.dark-mode .file-card--disabled:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .file-card--disabled .file-card__name {
  color: var(--app-text-faint);
}

.dark-mode .file-card--disabled .file-card__icon {
  background: var(--app-form-tile);
  color: var(--app-text-faint);
}

.dark-mode .file-card__icon-wrap::after {
  border: 2px solid var(--app-border);
}

.dark-mode .file-card--has-file .file-card__icon-wrap::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='var(--app-card)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='m4.5 12.75 6 6 9-13.5'/%3E%3C/svg%3E");
}

.dark-mode .file-card--empty .file-card__icon-wrap::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='var(--app-card)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M6 18 18 6M6 6l12 12'/%3E%3C/svg%3E");
}

.dark-mode .file-card__icon {
  background: var(--app-danger-bg);
}

.dark-mode .file-card__name {
  color: var(--app-text);
}

.dark-mode .file-card__hint {
  color: var(--app-text-faint);
}

.dark-mode .file-card__btn {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
}

.dark-mode .file-card__btn:hover {
  background: var(--app-form-tile-hover);
  color: var(--app-text);
}

.dark-mode .rv-datepicker__trigger {
  background: var(--app-field);
  border: 1px solid var(--app-field-border);
}

.dark-mode .rv-datepicker__icon {
  color: var(--app-text-muted);
}

.dark-mode .rv-datepicker__text {
  color: var(--app-text);
}

.dark-mode .rv-datepicker__placeholder {
  color: var(--app-text-faint);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE WRAPPER
   ============================================ */
.rv-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding: 24px 24px 48px;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ============================================
   HEADER CARD
   ============================================ */
.rv-header {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #eaecef;
  border-radius: 16px;
  padding: 14px 20px;
  margin-bottom: 20px;
}

.rv-header__back {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e2e4e8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}

.rv-header__back:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.rv-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.rv-header__spacer {
  flex: 1;
}

.rv-header__action {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e2e4e8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}

.rv-header__action:hover {
  background: #f0f9ff;
  border-color: #0ea5e9;
}

.rv-header__action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ============================================
   CARDS GRID
   ============================================ */
.rv-cards-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: start;
}

/* ============================================
   SECTION CARD
   ============================================ */
.rv-section {
  background: #eaecef;
  border-radius: 14px;
  overflow: hidden;
}

.rv-section__header {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  padding: 14px 22px;
}

.rv-section__title {
  font-size: 0.72rem;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.rv-section__body {
  background: #fff;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* ============================================
   FORM LAYOUT
   ============================================ */
.form-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.form-row--top {
  align-items: flex-start;
}

.form-row--sub {
  margin-top: 10px;
}

.form-label {
  flex: 0 0 180px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #334155;
  padding-top: 2px;
}

.form-label--sub {
  flex: 0 0 100px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
}

.form-field {
  flex: 1;
  min-width: 0;
}

/* ============================================
   TEXT INPUTS (Vuetify overrides)
   ============================================ */
.rv-input >>> .v-input__slot {
  background: #fff !important;
  border: 1px solid #e2e4e8 !important;
  border-radius: 8px !important;
  min-height: 40px !important;
  box-shadow: none !important;
  transition: all 0.15s ease;
}

.rv-input >>> .v-input__slot:hover {
  border-color: #c9cdd3 !important;
}

.rv-input >>> .v-input__slot::before,
.rv-input >>> .v-input__slot::after {
  display: none !important;
}

.rv-input >>> .v-text-field__slot input,
.rv-input >>> .v-text-field__slot textarea {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  color: #1a1e24 !important;
}

.rv-input >>> .v-text-field__slot input::placeholder,
.rv-input >>> .v-text-field__slot textarea::placeholder {
  color: #94a3b8 !important;
  font-weight: 500 !important;
}

.rv-input >>> .v-label {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.82rem !important;
  color: #94a3b8 !important;
}

.rv-input.v-input--is-focused >>> .v-input__slot {
  border-color: #0ea5e9 !important;
}

.rv-input--disabled >>> .v-input__slot {
  background: #f1f5f9 !important;
  border: 1px dashed #e2e8f0 !important;
  cursor: not-allowed !important;
}

.rv-input--disabled >>> .v-text-field__slot input,
.rv-input--disabled >>> .v-text-field__slot textarea {
  color: #94a3b8 !important;
}

/* v-select within rv-input */
.rv-input.v-select >>> .v-input__slot {
  background: #fff !important;
  border: 1px solid #e2e4e8 !important;
  border-radius: 8px !important;
}

.rv-input.v-select >>> .v-select__selection {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  color: #1a1e24 !important;
}

/* ============================================
   FILE CARD TAG (Invoice/CN number badge)
   ============================================ */
.file-card__tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 6px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  letter-spacing: 0.3px;
  white-space: nowrap;
  flex-shrink: 0;
}

.file-card__tag--amber {
  background: #fef3c7;
  color: #92400e;
}

/* ============================================
   FILE CARD
   ============================================ */
.file-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid #e2e4e8;
  border-radius: 12px;
  background: #f8fafc;
  transition: background 0.15s;
}

.file-card:hover {
  background: #f1f5f9;
}

.file-card--disabled {
  border: 1px dashed #e2e8f0;
  background: #f1f5f9;
  cursor: not-allowed;
}

.file-card--disabled:hover {
  background: #f1f5f9;
}

.file-card--disabled .file-card__name {
  color: #94a3b8;
}

.file-card--disabled .file-card__icon {
  background: #f1f5f9;
  color: #94a3b8;
}

.file-card__icon-wrap {
  position: relative;
  flex-shrink: 0;
}

.file-card__icon-wrap::after {
  content: '';
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #f8fafc;
  background-size: 10px;
  background-position: center;
  background-repeat: no-repeat;
}

.file-card--has-file .file-card__icon-wrap::after {
  background-color: #22c55e;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='m4.5 12.75 6 6 9-13.5'/%3E%3C/svg%3E");
}

.file-card--empty .file-card__icon-wrap::after {
  background-color: #ef4444;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='3' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M6 18 18 6M6 6l12 12'/%3E%3C/svg%3E");
}

.file-card__icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #fef2f2;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.file-card__svg {
  width: 20px;
  height: 20px;
}

.file-card__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.file-card__name {
  font-size: 0.82rem;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-card__hint {
  font-size: 0.7rem;
  color: #94a3b8;
}

.file-card__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.file-card__btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.file-card__btn:hover {
  background: #e2e8f0;
  color: #334155;
}

.file-card__btn-icon {
  width: 16px;
  height: 16px;
}

.file-card__hidden {
  display: none;
}

/* ============================================
   ERROR MESSAGES
   ============================================ */
.rv-select__error {
  margin-top: 6px;
  font-size: 0.8rem;
  color: #ef4444;
}

/* ============================================
   DATEPICKER TRIGGER (hidden, but styled)
   ============================================ */
.rv-datepicker__trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #e2e4e8;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  transition: border-color 0.15s;
}

.rv-datepicker__trigger:hover {
  border-color: #0ea5e9;
}

.rv-datepicker__icon {
  width: 20px;
  height: 20px;
  color: #64748b;
}

.rv-datepicker__text {
  font-size: 0.85rem;
  color: #334155;
  font-weight: 500;
}

.rv-datepicker__placeholder {
  font-size: 0.85rem;
  color: #94a3b8;
}

/* ============================================
   FADE TRANSITION
   ============================================ */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 960px) {
  .rv-cards-grid {
    grid-template-columns: 1fr;
  }

  .form-row {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }

  .form-label {
    flex: none;
  }

  .form-label--sub {
    flex: none;
  }

  .rv-page {
    padding: 16px 12px 48px;
  }
}

@media (max-width: 600px) {
  .rv-header__title {
    font-size: 1rem;
  }

  .file-card {
    flex-wrap: wrap;
    gap: 8px;
  }

  .file-card__actions {
    width: 100%;
    justify-content: flex-end;
  }
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .rv-page { background-color: var(--app-page); }
.dark-mode .rv-header { background: var(--app-card); }
.dark-mode .rv-header__title { color: var(--app-text); }
.dark-mode .rv-header__back { background: var(--app-form-tile); border-color: var(--app-form-tile-border); }
.dark-mode .rv-header__back:hover { background: var(--app-form-tile-hover); }
.dark-mode .rv-header__action { background: var(--app-form-tile); border-color: var(--app-form-tile-border); }
.dark-mode .rv-header__action:hover { background: var(--app-form-tile-hover); }
.dark-mode .rv-section { background: var(--app-card); }

/* --- Section headers. Light paints these with a saturated sky gradient
       (#0ea5e9 -> #38bdf8), which is the loudest thing on a dark page. Dark
       goes neutral, matching application/View.vue's `.section-header`: the
       band is the card's own tier carrying the same `inset 0 1px 0` highlight
       every other raised surface uses, so it reads as a header by GEOMETRY —
       a lifted lip above the brighter --app-elevated body, closed by
       --app-border — rather than by colour. The accent survives only as a 2px
       spine that fades out down the band. */
.dark-mode .rv-section__header {
  position: relative;
  background-color: var(--app-card);
  background-image: none;
  border-bottom: 1px solid var(--app-border);
  box-shadow: inset 0 1px 0 var(--app-inner-highlight);
}

/* .rv-section's overflow clips this to the card radius. */
.dark-mode .rv-section__header::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(
    180deg,
    rgba(var(--app-accent-rgb), 0.55),
    rgba(var(--app-accent-rgb), 0.12)
  );
}

.dark-mode .rv-section__title {
  color: var(--app-text);
  font-weight: 700;
  letter-spacing: 1.6px;
}

/* The content plane, one tier brighter than the header lip above it — same
   tiering as View.vue's `.section-body`. */
.dark-mode .rv-section__body { background: var(--app-elevated); }
.dark-mode .rv-cards-grid .form-label { color: var(--app-text-muted); }
.dark-mode .rv-input >>> .v-input__slot { background: var(--app-field) !important; border-color: var(--app-field-border) !important; }
.dark-mode .rv-input >>> .v-text-field__slot input,
.dark-mode .rv-input >>> .v-text-field__slot textarea,
.dark-mode .rv-input >>> .v-select__selection { color: var(--app-text) !important; }
.dark-mode .rv-input--disabled >>> .v-input__slot { background: var(--app-field-disabled) !important; border-color: var(--app-field-disabled-border) !important; }
.dark-mode .file-card { background: var(--app-form-tile); border-color: var(--app-form-tile-border); }
.dark-mode .file-card--disabled { background: var(--app-form-tile); border-color: var(--app-border); }
.dark-mode .file-card__name { color: var(--app-text); }
.dark-mode .file-card__hint { color: var(--app-text-faint); }
.dark-mode .file-card__btn { background: var(--app-form-tile); color: var(--app-text-muted); }
.dark-mode .file-card__tag { background: var(--app-accent-soft); color: var(--app-accent); }
.dark-mode .file-card__tag--amber { background: var(--app-warning-bg); color: var(--app-warning-fg); }

/* >>> dark companions: the light rules above pin these to a dark ink
   with !important, which on the dark field renders the text invisible
   rather than absent. Token references only. <<< */

.dark-mode .rv-input >>> .v-text-field__slot input,
.dark-mode .rv-input >>> .v-text-field__slot textarea {
  color: var(--app-text) !important;
}

.dark-mode .rv-input.v-select >>> .v-select__selection {
  color: var(--app-text) !important;
}

</style>

<style>
/* The unscoped `.v-menu__content …` block that used to sit here is gone.
   It was global — unscoped CSS is injected once and never removed, so a rule
   written for THIS page's dropdown repainted every menu in the app (with
   !important, and with hard-coded light hex) for the rest of the session. That
   is what made the same v-select look different depending on which page you had
   visited. Menus are defined ONCE, in tokens, in src/plugins/theme-glass.css §3
   — panel, list, item radius, hover, active, typography and the state overlay.
   A page that genuinely needs its own menu styling should give that v-menu a
   `content-class` and key off it, the way .bdt-menu and .tb-view-menu do. */

/* >>> end auto-dark <<< */

/* ============================================
   UNSCOPED: Datepicker overrides
   ============================================ */
.v-picker {
  border-radius: 12px !important;
}

.v-date-picker-table .v-btn.v-btn--active {
  background-color: #0ea5e9 !important;
}

/* ============================================
   UNSCOPED: Error message color fix
   ============================================ */
.v-messages__message {
  color: red;
}
</style>
